"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { OspToast } from "@ospweb/sdk-ui";
import Translations from "@translate/hooks/usePaymentMethodReturn";
import { QUERY_KEYS } from "@config/constant";
import { I18nUtil } from "@utils/i18n";
import { clearCheckoutStash } from "@utils/member/checkoutSessionStash";
import {
  getSubscriptionManageError,
  SUBSCRIPTION_MANAGE_CODE,
} from "@utils/member/subscriptionErrors";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { pubsub } from "utils";
import {
  confirmPaymentMethodSession,
  PM_SESSION_CANCEL,
  PM_SESSION_DONE,
  PM_SESSION_QUERY,
  type PmSessionOutcome,
} from "@hooks/useSubscriptionManage";

const I18n = I18nUtil.createScoped(Translations);

const showOutcomeToast = (outcome: PmSessionOutcome): void => {
  switch (outcome) {
    case "applied":
      OspToast.success({ content: I18n.t("Payment method updated.") });
      return;
    case "pending":
      OspToast.info({
        content: I18n.t("We're updating your payment method…"),
      });
      return;
    case "expired":
      // 与 146206 同一文案出口
      OspToast.error({
        content: getSubscriptionManageError({
          code: SUBSCRIPTION_MANAGE_CODE.PAYMENT_METHOD_SESSION_NOT_FOUND,
        }).message,
      });
      return;
    default:
      return;
  }
};

/**
 * 换卡托管页回跳（EDG-552，fe-spec §3.3、§4.6 F8）：读 `?pmSession=done|cancel&planId=`，
 * 消费后立即 replaceState 清参（刷新页面不会重复提示）；结束后刷新 plans，扣款失败横幅随之消失。
 *
 * @param cleanPath 清参后的路径（`/member` 或 `/member/subscription`）
 */
export function usePaymentMethodReturn(cleanPath: string): void {
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const pmSession = searchParams.get(PM_SESSION_QUERY);
  const planId = searchParams.get("planId");
  const handledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !planId ||
      (pmSession !== PM_SESSION_DONE && pmSession !== PM_SESSION_CANCEL)
    ) {
      return;
    }
    const key = `${pmSession}_${planId}`;
    if (handledKeyRef.current === key) {
      return;
    }
    handledKeyRef.current = key;
    window.history.replaceState({}, "", cleanPath);
    if (pmSession === PM_SESSION_CANCEL) {
      clearCheckoutStash("payment_method", planId);
      return;
    }
    const run = async () => {
      const outcome = await confirmPaymentMethodSession({ planId });
      showOutcomeToast(outcome);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYMENT_LIST_PAYMENT],
      });
      // CurrentPlan 扣款失败横幅经 getListPayment + pubsub 取数（不走上面的 query key），不带数据即触发其重拉
      pubsub.emit(pubSubKeyMap.MEMBER_PAYMENT_ERROR);
    };
    void run();
  }, [pmSession, planId, cleanPath, queryClient]);
}
